/**
 * @module app.cd
 */

import { countMemberFilters, IMemberSearchFilters, MemberService } from "app/cd/services/member-service";
import { EssAccordionCustomAttribute } from 'app/shared/attributes/ess-collapse-attributes';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { MemberFilters } from 'app/shared/config/modules';
import { IPopup } from 'app/shared/elements/popup-base';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { validate } from 'app/shared/elements/ess-rule';
import { apiCall } from 'app/shared/lib/api/api';
import { debounce } from 'app/shared/lib/decorators';
import { ComponentBase, IForm, SortDirection, Sorting, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { Toaster } from 'app/shared/lib/toaster';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { autoinject, bindingMode } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IBaseConfig } from 'app/core/models/essette-interfaces';
import { MemberDisplay } from 'app/cd/elements/members/member-display';
import { guid } from 'app/shared/lib/guid';
import { EventAggregator } from 'aurelia-event-aggregator';
import { SetFocusEvent } from 'app/shared/lib/events';
import { requiredFields as memberRequiredFields } from 'app/cd/elements/members/member-display';
import * as CD from 'app/cd/models/essette-api';
import { NamespaceOptions } from 'app/shared/lib/i18n';
import { MemberPlanChangeEvent } from 'app/cd/lib/events';

@autoinject
export class MemberPlanSelect extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ defaultBindingMode: bindingMode.twoWay })
    protected memberPlan: CD.MemberPlan | null = null;

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected searchFilters: IMemberSearchFilters = {};

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected tempSearchFilters: IMemberSearchFilters = {};

    @bindable.boolean()
    public readonly: boolean = false;

    @bindable.boolean()
    public required: boolean = true;

    @bindable.boolean()
    public showValidator: boolean = false;

    @bindable.boolean()
    public exactMatch: boolean = false;

    @bindable.boolean()
    public allowGeneralSearch: boolean = true;

    @bindable.boolean()
    public primaryPlanAutoSelectable: boolean = false;

    @bindable.string
    public i18nNamespace: Extract<NamespaceOptions, NamespaceOptions.CD | NamespaceOptions.PP> = NamespaceOptions.CD;

    @bindable.boolean
    protected autoSelectOnlyResult: boolean = true;


    /** requiredFilters: string that defines filter requirement when searching
     * - A pipe (`|`) separates an OR group of filters
     * - A space (`' '`) is an AND group of filters
     *
     * e.g. `'identifier.primary|name.first birthdate'`  translates to "Primary Identifier OR (first name AND birthday)""
    */
    @bindable.stringNull()
    public requiredFilters: string | null = null;

    @bindable.objectNull()
    public availableFilters: string[] | null = [
        'active', 'identifier.primary', 'identifier.medicaid', 'name.first', 'name.last', 'birthDate', 'primaryCarePhysician',
        'primaryCarePhysician.name.first', 'primaryCarePhysician.name.last', 'healthPlan', 'lineOfBusiness'
    ];

    @bindable.objectNull()
    public config: IBaseConfig | null = null;

    private memberId: string | null = null;
    protected memberPlanResults: ReadonlyArray<CD.MemberPlan> | null = null;

    protected isValidSearch: boolean = false;

    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected hasIneligibleMembers: boolean = false;

    protected validateForm = validate;

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $memberDisplay!: MemberDisplay;
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $collapsible!: EssAccordionCustomAttribute;
    protected $searchCriteriaHeader!: HTMLElement;
    protected accordionElement!: HTMLUListElement;
    protected $currentCriteriaHeader!: HTMLElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        protected transitional: TransitionalManager,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        private readonly memberFilters: MemberFilters,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
    }

    protected attached(): void {
        if(this.config) {
            this.memberService.configure(this.config);
        }

        this.$collapsible.defaultIndex = 0; //and then, the collapsible's attached() shall run and open this
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.search(true);
    }

    public async search(loadPrimaryPlan?: boolean, openModal: boolean = false): Promise<void> {
        if(this.memberId && this.searchFilters) {
            this.searchFilters.id = this.memberId;
        }

        await this.searchMemberPlans(loadPrimaryPlan, openModal);
        this.events.publish(new SetFocusEvent(this.$currentCriteriaHeader));
    }

    protected async submitSearch() {
        this.resultCount = 0;
        if(this.$pager) this.$pager.updatePage(0);
        this.searchFilters = { ...this.tempSearchFilters };
        await this.search(undefined, true);

        if(this.memberPlanResults?.length === 1 && this.autoSelectOnlyResult) {
            this.selectMemberPlan(this.memberPlanResults[0]);
        }
    }

    protected showGeneralSearch(): boolean {
        if(this.allowGeneralSearch) return true;
        if(!this.requiredFilters) return false; // JP | we don't want to show it while the filters are loading

        const { requirePrimary, requireMedicaid } = this.findRequiredIdentifierFields();
        return requirePrimary || requireMedicaid;
    }

    /** Returns flags for which member identifiers have been configured as `required` */
    private findRequiredIdentifierFields(): { requirePrimary: boolean, requireMedicaid: boolean } {
        if(!this.requiredFilters) return { requirePrimary: false, requireMedicaid: false }

        const requiredArray = this.requiredFilters.split('|');
        let requirePrimary = false, requireMedicaid = false;

        requiredArray.forEach(requirements => {
            requirePrimary = requirePrimary || requirements === 'identifier.primary';
            requireMedicaid = requireMedicaid || requirements === 'identifier.medicaid';
        });
        return { requirePrimary, requireMedicaid };
    };

    /** Called by the view to get the placeholder text in the search box.
     * When not allowing general search (except exact ID matches), the text updates based on whether primary, medicaid, or both identifiers are enabled */
    protected getIdentifierSearchText(): string {
        if(!this.allowGeneralSearch) {
            const { requirePrimary, requireMedicaid } = this.findRequiredIdentifierFields();
            let label: string = `${this.i18nNamespace}:member.identifier.$long`;
            if(requirePrimary && requireMedicaid) {
                return label;
            } else if(requirePrimary) {
                label = `${this.i18nNamespace}:member.identifier.primary.$`;
            } else if(requireMedicaid) {
                label = `${this.i18nNamespace}:member.identifier.medicaid.$`;
            }
            return label;
        }
        else {
            return `${this.i18nNamespace}:member.$search`;
        }
    }

    public async generalSearch(searchTerm: string): Promise<void> {
        if(searchTerm) {
            const { requirePrimary, requireMedicaid } = this.findRequiredIdentifierFields();
            if(!this.allowGeneralSearch) {
                // clear general search term when exact searching is enforced
                this.searchFilters.memberId = (requirePrimary && !requireMedicaid) ? searchTerm : undefined;
                this.searchFilters.medicaidId = (requireMedicaid && !requirePrimary) ? searchTerm : undefined;
                this.searchFilters.generalIdentifier = (requireMedicaid && requirePrimary) ? searchTerm : undefined;
                this.searchFilters.generalSearchTerm = undefined;
            } else {
                // clear member ID search when general searching is allowed
                this.searchFilters.memberId = undefined;
                this.searchFilters.generalSearchTerm = searchTerm;
            }
        }

        this.searchMemberPlan();
    }

    protected refreshResults(): void {
        this.search(true);
    }

    public selectMemberPlan(memberPlan: CD.MemberPlan) {
        this.searchFilters = {}
        this.memberPlan = memberPlan;
        this.events.publish(new MemberPlanChangeEvent(memberPlan));
        if(this.$memberDisplay) {
            this.$memberDisplay.setPlan(memberPlan);
        }
        this.$modal.close();
        this.clear();
    }

    public clear(returnFocus?: boolean): void {
        this.memberPlanResults = new Array<CD.MemberPlan>();
        this.searchFilters = {};
        this.tempSearchFilters = {};
        this.resultCount = 0;
        this.memberId = null;
        this.$collapsible.open(0);
        this.processSearchCriteria();
        if(returnFocus) this.events.publish(new SetFocusEvent(this.$searchCriteriaHeader));
    }

    protected close(): void {
        this.clear();
        this.$modal.close();
    }

    protected memberPlanCleared(): void {
        this.memberPlan = null;
        this.searchFilters.generalSearchTerm = null;
        if(this.$memberDisplay) {
            this.$memberDisplay.clear();
        }
    }

    public async searchMemberPlan(memberId?: string): Promise<void> {
        this.memberId = memberId || this.memberId;

        if(this.searchFilters) {
            await this.search(true);
        }

        if(this.memberPlanResults?.length === 1) {
            this.selectMemberPlan(this.memberPlanResults[0]);
        }
        else {
            this.$modal.open();
            this.processSearchCriteria();
        }
    }

    @debounce(100)
    protected processSearchCriteria() {
        const atLeastOneFilterApplied = countMemberFilters(this.tempSearchFilters) > 0;
        if(!this.requiredFilters && atLeastOneFilterApplied) {
            this.isValidSearch = true;
            return;
        }

        const requiredArray = this.requiredFilters?.split('|') ?? [];
        this.isValidSearch = requiredArray.some(requirements => {
            const requirementsArray = requirements.split(' ');
            return requirementsArray.every(requirement => this.isRequirementMet(requirement));
        });
    }

    protected isRequirementMet(requirement: string): boolean {
        switch(requirement) {
            case 'identifier.primary':
                return !!this.tempSearchFilters?.memberId;
            case 'identifier.medicaid':
                return !!this.tempSearchFilters?.medicaidId;
            case 'name.first':
                return !!this.tempSearchFilters?.memberFirstName;
            case 'name.last':
                return !!this.tempSearchFilters?.memberLastName;
            case 'birthDate':
                return !!this.tempSearchFilters?.dob;
            case 'primaryCarePhysician':
                return !!this.tempSearchFilters?.pcpId;
            case 'primaryCarePhysician.name.first':
                return !!this.tempSearchFilters?.pcpFirstName;
            case 'primaryCarePhysician.name.last':
                return !!this.tempSearchFilters?.pcpLastName;
            case 'healthPlan':
                return !!this.tempSearchFilters?.healthPlan;
            case 'lineOfBusiness':
                return !!this.tempSearchFilters?.lineOfBusiness;
        }

        return this.memberFilters.getFilters().every(filter => {
            if(requirement === filter.filterName) {
                return !!filter.viewModel;
            }

            return true;
        });
    }

    public searchAndOpenModal() {
        this.search(false);
        this.$modal.open();
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public submittedChanges?: any;
    public async submit(): Promise<boolean> {
        return true;
    }

    public reset?(): void {
        this.clear();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async searchMemberPlans(loadPrimaryPlan?: boolean, openModal?: boolean): Promise<void> {
        if(countMemberFilters(this.searchFilters) === 0) {
            this.$collapsible.open(0);
            if(openModal) {
                this.toaster.warn().message(this.i18n.tr('ui:messages.warnings.minimumFilterRequirements')).show();
            }
            this.memberPlanResults = [];
            return;
        } else {
            this.$collapsible.open(1);
        }

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: false
        };

        try {
            this.state.beginLoad();

            this.searchFilters.exactMatch = this.exactMatch;
            const includes = [
                'healthPlan',
                'lineOfBusiness',
                'member',
                'member.primaryCarePhysician'
            ];

            const fields = [
                'eligibility.isCurrent',
                'healthPlan.description',
                'identifier.primary',
                'isPrimary',
                'lineOfBusiness.description',
                'member.birthDate',
                'member.name.full',
                'member.primaryCarePhysician.identifier.primary',
                'member.primaryCarePhysician.name.full'
            ];

            memberRequiredFields.forEach(field => !fields.includes(`member.${field}`) ? fields.push(`member.${field}`) : null);

            const response = await this.memberService.searchMemberPlans(this.searchFilters, options, includes, fields);

            let primaryPlan;
            const memberResults = (this.primaryPlanAutoSelectable && loadPrimaryPlan) && (primaryPlan = response.results.find(m => m.isPrimary)) ? [primaryPlan] : response.results;
            this.memberPlanResults = memberResults;
            this.resultCount = response.count || 0;

            // If results include some ineligible members, show the warning column
            this.hasIneligibleMembers = memberResults.some(m => { return m.eligibility && !m.eligibility.isCurrent; });
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
